import React, { useState, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useGlobalStorageContext } from '../contexts/GlobalStorageContext';
import { getHijriDate } from '../utils/hijriDate';
import './HijriPage.css';

export const HijriPage: React.FC = () => {
  const { colors, isDarkMode } = useTheme();
  const { t } = useLanguage();
  const { hijriAdjustment, setHijriAdjustment } = useGlobalStorageContext();
  const [calendarDate, setCalendarDate] = useState(new Date());

  const getHijriDetails = (date: Date) => getHijriDate(date, hijriAdjustment);

  const hijriCalendar = useMemo(() => {
    const details = getHijriDetails(calendarDate);
    const days: Date[] = [];
    let search = new Date(calendarDate);
    search.setDate(search.getDate() - 32);
    
    while (getHijriDetails(search).month !== details.month) {
      search.setDate(search.getDate() + 1);
    }

    const startDayOfWeek = search.getDay();
    
    while (getHijriDetails(search).month === details.month) {
      days.push(new Date(search));
      search.setDate(search.getDate() + 1);
    }

    return { days, startDayOfWeek, monthName: details.month, year: details.year };
  }, [calendarDate, hijriAdjustment]);

  const todayHijri = getHijriDetails(new Date());

  const getMonthVirtues = (month: string) => {
    switch (month) {
      case 'Muharram': return ["First month of Islamic Year", "Day of Ashura (10th)"];
      case 'Ramadan': return ["The Month of Fasting", "Laylat al-Qadr in last 10 nights"];
      case 'Shawwal': return ["Eid al-Fitr (1st)", "Recommended to fast 6 days"];
      case 'Dhu al-Hijjah': case 'Dhū al-Ḥijjah': return ["The Month of Hajj", "Eid al-Adha (10th)", "First 10 days are sacred"];
      case 'Rajab': return ["One of the 4 Sacred Months", "Month of Al-Isra' wal-Mi'raj"];
      case "Sha'ban": return ["Preparation for Ramadan", "Mid-Sha'ban (Night of Bara'at)"];
      default: return ["A blessed month", "The White Days (13, 14, 15) for Sunnah fasting"];
    }
  };

  return (
    <div className="hijri-page" style={{ backgroundColor: colors.background, color: colors.text }}>
      <div className="hijri-header" style={{ backgroundColor: colors.tint }}>
        <div className="header-label">{t.more.now}</div>
        <div className="header-date">{`${todayHijri.day} ${todayHijri.month} ${todayHijri.year}`}</div>
      </div>

      <div className="adjustment-bar" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
        <label>{t.more.adjustHijri}:</label>
        <div className="adjustment-buttons">
          {[-2, -1, 0, 1, 2].map((adj) => (
            <button
              key={adj}
              onClick={() => setHijriAdjustment(adj)}
              className={hijriAdjustment === adj ? 'active' : ''}
              style={{
                backgroundColor: hijriAdjustment === adj ? colors.tint : colors.background,
                color: hijriAdjustment === adj ? '#fff' : colors.text,
                borderColor: colors.border
              }}
            >
              {adj > 0 ? `+${adj}` : adj}
            </button>
          ))}
        </div>
      </div>

      <div className="calendar-nav">
        <div>
          <div className="month-name" style={{ color: colors.tint }}>{hijriCalendar.monthName} {hijriCalendar.year}</div>
          <div className="cycle-text" style={{ color: colors.muted }}>{t.more.hijriCycle}</div>
        </div>
        <div className="nav-buttons">
          <button onClick={() => setCalendarDate(new Date(calendarDate.getTime() - 30 * 24 * 60 * 60 * 1000))} style={{ backgroundColor: colors.cardBackground, borderColor: colors.border, color: colors.tint }}>‹</button>
          <button onClick={() => setCalendarDate(new Date())} style={{ backgroundColor: colors.cardBackground, borderColor: colors.border, color: colors.tint }}>{t.more.now}</button>
          <button onClick={() => setCalendarDate(new Date(calendarDate.getTime() + 30 * 24 * 60 * 60 * 1000))} style={{ backgroundColor: colors.cardBackground, borderColor: colors.border, color: colors.tint }}>›</button>
        </div>
      </div>

      <div className="calendar" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
        <div className="week-days">
          {t.more.weekDays.map((d, i) => (
            <div key={i} className="week-day" style={{ color: colors.tint }}>{d}</div>
          ))}
        </div>
        <div className="calendar-grid">
          {[...Array(hijriCalendar.startDayOfWeek)].map((_, i) => (
            <div key={`empty-${i}`} className="calendar-day" />
          ))}
          {hijriCalendar.days.map((date, i) => {
            const details = getHijriDetails(date);
            const isToday = date.toDateString() === new Date().toDateString();
            const isWhiteDay = [13, 14, 15].includes(details.day);
            
            return (
              <div
                key={i}
                className={`calendar-day ${isToday ? 'today' : ''} ${isWhiteDay && !isToday ? 'white-day' : ''}`}
                style={{
                  backgroundColor: isToday ? colors.tint : isWhiteDay ? (isDarkMode ? '#422006' : '#fef3c7') : 'transparent',
                  color: isToday ? '#fff' : colors.text,
                  borderColor: isWhiteDay && !isToday ? colors.accent : 'transparent'
                }}
              >
                <div className="day-number">{details.day}</div>
                <div className="gregorian-day" style={{ color: isToday ? 'rgba(255,255,255,0.7)' : colors.muted }}>{date.getDate()}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="month-virtues" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
        <div className="virtues-title" style={{ color: colors.tint }}>{t.more.highlights}</div>
        {getMonthVirtues(hijriCalendar.monthName).map((v, i) => (
          <div key={i} className="virtue-item">
            <div className="virtue-bullet" style={{ backgroundColor: colors.tint }} />
            <div className="virtue-text" style={{ color: colors.text }}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
